import React from 'react';
import {AccentColorOptions, useBreakpointOnly} from 'epic-ue-theme';
import {
    CmsComponentMapper,
    CmsDynamicArray,
    GridContainer,
    Image,
    ImageCard,
    ImageCardProps,
    VerticalSpacer
} from '../../elements';
import {LineTextBlock, LineTextBlockProps} from '../../elements';
import {AnimateOnViewWrap, UeLink, WithCaption} from '../../wraps/';
import Grid, {GridJustification, GridSize} from '@material-ui/core/Grid';
import styled from 'styled-components';

const Styled = styled.div`
    && {
        .multi-card-content {
            padding-top: 0;
        }
        .ue-link-wrap {
            img:hover {
                transition: transform 0.5s;
                transform: scale(1.1);
            }
        }
        .image-card {
            .paragraph,
            .heading {
                font-weight: 400;
            }
        }

        ${(p) => p.theme.breakpoints.down('sm')} {
            .extraPadding {
                padding-bottom: 4px;
            }
        }
    }
`;

export interface MultiCardOptions extends ImageCardProps {
    numCards?: number;
    accentColor?: AccentColorOptions;
    cardAnimationDelay?: number;
}

export type MultiCardOptionsArray = CmsDynamicArray<MultiCardOptions>;

export interface MultiCardProps {
    cards?: MultiCardOptionsArray;
    lineTextBlock?: LineTextBlockProps;
    accentColor?: AccentColorOptions;
    maxItemsInRowFromMd?: number;
}

const MultiCardWrapper = (props: MultiCardOptions): JSX.Element | null => {
    const {numCards = 1, accentColor, cardAnimationDelay = 0, ...cardProps} = props;
    const md = (12 / numCards) as GridSize;
    const sm = numCards === 1 ? 12 : 6;

    if (!cardProps.title && !cardProps.description && !cardProps.image?.src) {
        return null;
    }

    return (
        <Grid
            item
            md={md}
            sm={sm}
            xs={12}
            className={`multi-card-wrap ${cardProps.image ? 'multi-card-content' : ''}`}>
            <AnimateOnViewWrap translateY={50} delay={cardAnimationDelay}>
                {cardProps.title ||
                cardProps.description ||
                cardProps.image?.src ||
                cardProps.links?.length ? (
                    <>
                        <ImageCard
                            {...cardProps}
                            image={undefined}
                            accentColor={accentColor}
                            paragraphCaption={false}
                        />
                        <div className="extraPadding">
                            <VerticalSpacer variant="custom" spacing={3} />
                        </div>
                    </>
                ) : (
                    ''
                )}
            </AnimateOnViewWrap>
        </Grid>
    );
};

const ImageWrapper = ({image, caption, link, numCards = 1}: MultiCardOptions): JSX.Element => {
    const md = (12 / numCards) as GridSize;
    const sm = numCards === 1 ? 12 : 6;
    const disableCaption = numCards > 3;

    const captionText = `${caption?.text || ''}`;
    let imageTitle = image?.title || '';
    let forceBelow = Boolean(caption?.forceBelow);
    if (disableCaption) {
        forceBelow = false;
        if (!imageTitle && captionText) {
            imageTitle = captionText;
        }
    }

    const imageEl = (
        <WithCaption {...caption} forceBelow={forceBelow} text={disableCaption ? '' : captionText}>
            <Image
                {...image}
                title={imageTitle}
                ratio={image?.ratio}
                optMaxWidth={1600 / numCards}
            />
        </WithCaption>
    );
    const innerElem = link ? (
        <UeLink href={link.href} title={disableCaption ? captionText : ''}>
            {imageEl}
        </UeLink>
    ) : (
        imageEl
    );

    return image && image.src ? (
        <Grid item md={md} sm={sm} xs={12}>
            {innerElem}
        </Grid>
    ) : (
        <></>
    );
};

export const MultiCard = ({
    cards = [],
    lineTextBlock = {},
    accentColor,
    maxItemsInRowFromMd = 4
}: MultiCardProps): JSX.Element => {
    const isSm = useBreakpointOnly('sm');
    const isXs = useBreakpointOnly('xs');

    let maxItemsInRow = maxItemsInRowFromMd;
    if (isXs) {
        maxItemsInRow = 1;
    } else if (isSm) {
        maxItemsInRow = 2;
    }

    const justifyContent = 'flex-start' as GridJustification;

    const numCards = cards.length > maxItemsInRow ? maxItemsInRow : cards.length;
    const extraProps = {
        accentColor,
        numCards
    };

    const getGridItems = (count: number) => {
        const elements: JSX.Element[] = [];
        let partialCards: MultiCardOptionsArray = [];

        const cardDelay = 0.2;
        cards.forEach((card, key) => {
            const cardAnimationDelay = cardDelay + key * cardDelay;
            if (card.text) {
                card.text.cardAnimationDelay = cardAnimationDelay;
            }

            partialCards.push(card);

            if ((key + 1) % count === 0 || key + 1 === cards.length) {
                elements.push(
                    <React.Fragment key={key}>
                        <Grid
                            container
                            alignItems={'flex-end'}
                            justifyContent={justifyContent}
                            spacing={3}>
                            <CmsComponentMapper
                                dynamicPageDataArray={partialCards}
                                elementMapping={{
                                    image: <ImageWrapper {...extraProps} />,
                                    link: <ImageWrapper {...extraProps} />,
                                    text: <ImageWrapper {...extraProps} />
                                }}
                            />
                        </Grid>
                        <Grid
                            className="multicard-grid-item"
                            container
                            alignItems={'flex-start'}
                            justifyContent={justifyContent}
                            spacing={3}>
                            <CmsComponentMapper
                                dynamicPageDataArray={partialCards}
                                elementMapping={{
                                    image: <MultiCardWrapper {...extraProps} showButton={true} />,
                                    link: (
                                        <MultiCardWrapper
                                            {...extraProps}
                                            showTopAccentColorLine={true}
                                        />
                                    ),
                                    text: (
                                        <MultiCardWrapper
                                            {...extraProps}
                                            showButton={true}
                                            showTopAccentColorLine={true}
                                        />
                                    )
                                }}
                            />
                        </Grid>
                    </React.Fragment>
                );
                partialCards = [];
            }
        });
        return elements;
    };

    return (
        <Styled>
            <GridContainer className="multicard-grid-container">
                {lineTextBlock && lineTextBlock.headline ? (
                    <>
                        <LineTextBlock
                            {...lineTextBlock}
                            accentColor={accentColor}
                            button={
                                lineTextBlock.button && {
                                    ...lineTextBlock.button,
                                    type: 'tertiary'
                                }
                            }
                        />
                        <VerticalSpacer variant="custom" spacing={5} downSmSpacing={3} />
                    </>
                ) : (
                    ''
                )}
                {getGridItems(maxItemsInRow)}
            </GridContainer>
        </Styled>
    );
};
